/**
 * The reinstatement vocabulary (SR-313), **a mirror** of survey-api's `OptionLifetimes` and
 * `ReinstatementHandlings` (its reinstatement design §2.2): a reason's lifecycle class — PRS v1.6
 * §9.2's, and whether the worklist reconsiders it each cycle — and how the worklist handles a
 * reason's group. The worklist and a Quality Rating's card read the one table.
 */

export interface IReasonClass {
  value: string;
  label: string;
  /** Reconsidered on the worklist each cycle (`OptionLifetimes.Worklist`). */
  worklist: boolean;
  /** The class in a word or two — a reason's card. */
  short: string;
}

export const REASON_CLASSES: readonly IReasonClass[] = [
  { value: 'single_cycle', label: 'Temporary — single cycle', worklist: true, short: 'Single cycle' },
  { value: 'each_cycle', label: 'Temporary — reviewed each cycle', worklist: true, short: 'Each cycle' },
  { value: 'monthly', label: 'Temporary — reviewed monthly', worklist: true, short: 'Monthly' },
  { value: 'catch_all', label: 'Temporary — catch-all', worklist: true, short: 'Catch-all' },
  { value: 'on_resubmission', label: 'Indefinite — reviewed on resubmission', worklist: true, short: 'On resubmission' },
  { value: 'on_trigger', label: 'Indefinite — reviewed on trigger', worklist: false, short: 'On trigger' },
  { value: 'indefinite', label: 'Indefinite', worklist: false, short: 'Indefinite' },
  { value: 'legacy', label: 'Legacy — reporting only', worklist: false, short: 'Legacy' }
];

export interface IReinstatementHandling {
  value: string;
  label: string;
  /** What the worklist's section of such a reason offers. */
  says: string;
  /** The same in a few words — a reason's card. */
  hint: string;
}

export const REINSTATEMENT_HANDLINGS: readonly IReinstatementHandling[] = [
  {
    value: 'bulk',
    label: 'Reinstate in bulk',
    says: 'Reinstate the whole group in one action.',
    hint: 'reinstate all at once'
  },
  {
    value: 'bulk_except_noted',
    label: 'In bulk, except noted',
    says: 'Reinstate the group in one action — except the rows whose notes say to sit out the next ranking too.',
    hint: 'all at once, except noted'
  },
  {
    value: 'per_record',
    label: 'Read the notes, per record',
    says: 'Read the notes and decide row by row.',
    hint: 'read the notes'
  },
  {
    value: 'firm_compare',
    label: 'Compare the firms',
    says: 'Reinstate where the advisor has edited since and the survey’s firm matches the one BrokerCheck names.',
    hint: 'compare firms'
  },
  {
    value: 'edit_vs_outreach',
    label: 'Last edit against outreach',
    says: 'Reinstate where the advisor has edited the survey since being marked.',
    hint: 'edits vs outreach'
  },
  {
    value: 'u4_review',
    label: 'Review the disclosure',
    says: 'Reinstate where the pending matter has settled; otherwise the compliance queue reviews it.',
    hint: 'review the disclosure'
  },
  { value: 'none', label: 'Not reinstated', says: 'Not reinstated from this list.', hint: 'not reinstated here' }
];

export const WORKLIST_CLASSES: readonly string[] = REASON_CLASSES.filter((one) => one.worklist).map((one) => one.value);

/** How a section of `handling` is handled; a handling this table lacks is read per record. */
function handlingOf(handling: string): IReinstatementHandling {
  return (
    REINSTATEMENT_HANDLINGS.find((one) => one.value === handling) ??
    REINSTATEMENT_HANDLINGS.find((one) => one.value === 'per_record')!
  );
}

/** What a section of `handling` offers. */
export function handlingSays(handling: string): string {
  return handlingOf(handling).says;
}

/** The same in a few words, for a reason's card. */
export function handlingHint(handling: string): string {
  return handlingOf(handling).hint;
}
